// Retention tab — the install's retention catalogue
// (`/api/v1/settings/retention`): kinds of documents and how long each is
// kept (an article, a title in each language, the period and the EPK mark),
// grouped in sections. The national standard list is offered as an import
// that adds only what the catalogue lacks; a
// spreadsheet in the export's shape imports too (checked first). A class
// some case uses is deactivated rather than deleted. Every change is in the
// Audit tab. Records management itself is switched on in Policies › Records.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Archive, Download, FileUp, Library, Loader2, Pencil, Plus, Power, Rows3, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { PERIOD_KINDS, TITLE_LANGUAGES, periodLabel, titleIn } from '@/modules/records/lib/retention.js';
import { LIMITS, classProblems, sectionProblems } from '@/modules/records/lib/validation.js';
import { useFieldErrors } from '@/shared/hooks/useFieldErrors.js';
import { FieldError, REQUIRED_LABEL } from '@/shared/ui/FieldError.jsx';
import { api, downloadFile, ep } from '../lib/api.js';

const CLASS_FIELDS = ['article', 'section_uuid', 'title', 'period_kind', 'period_years', 'epk', 'note'];
const SECTION_FIELDS = ['code', 'title', 'position'];
import { ConfirmDialog } from './ConfirmDialog.jsx';

const ALL = 'all';
const NO_SECTION = 'none';

function TitleFields({ value, onChange, idPrefix, errors = {} }) {
  const { t } = useTranslation('console');
  return (
    <fieldset className="space-y-2">
      <legend className={`text-sm font-medium ${REQUIRED_LABEL}`}>{t('retention.form.title')}</legend>
      <p className="text-xs text-muted-foreground">{t('retention.form.titleHint')}</p>
      <div className="grid gap-2 sm:grid-cols-2">
        {TITLE_LANGUAGES.map((lang) => (
          <div key={lang} className="space-y-1">
            <Label htmlFor={`${idPrefix}-${lang}`} className="text-xs text-muted-foreground">{t(`retention.languages.${lang}`)}</Label>
            <Input
              id={`${idPrefix}-${lang}`}
              maxLength={LIMITS.title}
              value={value[lang] || ''}
              aria-invalid={errors[`title.${lang}`] ? true : undefined}
              onChange={(e) => onChange({ ...value, [lang]: e.target.value })}
            />
            <FieldError id={`${idPrefix}-${lang}-error`} message={errors[`title.${lang}`]} />
          </div>
        ))}
      </div>
      <FieldError id={`${idPrefix}-error`} message={errors.title} />
    </fieldset>
  );
}

const hasTitle = (title) => Object.values(title || {}).some((v) => (v || '').trim());

function ClassForm({ open, onOpenChange, initial, sections, onSave }) {
  const { t, i18n } = useTranslation(['console', 'records']);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const checks = useFieldErrors(form ? classProblems(form) : {});
  const { reset } = checks;

  useEffect(() => {
    if (!open) return;
    setForm({
      article: initial?.article || '',
      section_uuid: initial?.section_uuid ? String(initial.section_uuid) : NO_SECTION,
      title: { ...(initial?.title || {}) },
      period_kind: initial?.period_kind || 'years',
      period_years: initial?.period_years ?? 5,
      epk: Boolean(initial?.epk),
      note: initial?.note || '',
    });
    reset();
  }, [open, initial, reset]);

  if (!form) return null;
  const set = (patch) => {
    setForm((prev) => ({ ...prev, ...patch }));
    Object.keys(patch).forEach((key) => checks.touch(key));
  };
  const years = Number(form.period_years);
  const valid = form.article.trim() && hasTitle(form.title) && !checks.blocked;
  const { errors } = checks;

  const submit = async () => {
    if (!checks.attempt()) return;
    setBusy(true);
    try {
      const res = await onSave({
        article: form.article.trim(),
        section_uuid: form.section_uuid === NO_SECTION ? null : form.section_uuid,
        title: form.title,
        period_kind: form.period_kind,
        period_years: form.period_kind === 'years' ? years : null,
        epk: form.epk,
        note: form.note.trim() || null,
      });
      if (res && !res.success) checks.showServer(res, CLASS_FIELDS);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !busy && onOpenChange(o)}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t(initial ? 'retention.form.editTitle' : 'retention.form.createTitle')}</DialogTitle>
          <DialogDescription>{t('retention.form.description')}</DialogDescription>
        </DialogHeader>
        <div className="max-h-[65vh] space-y-4 overflow-y-auto pr-1">
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="retention-article" className={REQUIRED_LABEL}>{t('retention.form.article')}</Label>
              <Input id="retention-article" required maxLength={LIMITS.code} value={form.article}
                onChange={(e) => set({ article: e.target.value })} placeholder="1.12" {...checks.describe('article', 'retention')} />
              <FieldError id="retention-article-error" message={errors.article} />
            </div>
            <div className="space-y-1.5">
              <Label>{t('retention.form.section')}</Label>
              <Select value={form.section_uuid} onValueChange={(v) => set({ section_uuid: v })}>
                <SelectTrigger aria-label={t('retention.form.section')}><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_SECTION}>{t('retention.noSection')}</SelectItem>
                  {sections.map((s) => (
                    <SelectItem key={s.uuid} value={String(s.uuid)}>{s.code} · {titleIn(s.title, i18n.language)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <TitleFields value={form.title} onChange={(title) => set({ title })} idPrefix="retention-title" errors={errors} />
          <div className="grid gap-3 sm:grid-cols-[1fr_8rem]">
            <div className="space-y-1.5">
              <Label>{t('retention.form.period')}</Label>
              <Select value={form.period_kind} onValueChange={(v) => set({ period_kind: v })}>
                <SelectTrigger aria-label={t('retention.form.period')}><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PERIOD_KINDS.map((k) => (
                    <SelectItem key={k} value={k}>{t(`records:period.kindNames.${k}`)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {form.period_kind === 'years' && (
              <div className="space-y-1.5">
                <Label htmlFor="retention-years">{t('retention.form.years')}</Label>
                <Input
                  id="retention-years"
                  type="number"
                  required
                  min={1}
                  max={LIMITS.periodYearsMax}
                  step={1}
                  value={form.period_years}
                  onChange={(e) => set({ period_years: e.target.value })}
                  {...checks.describe('period_years', 'retention')}
                />
                <FieldError id="retention-period_years-error" message={errors.period_years} />
              </div>
            )}
          </div>
          <label className="flex items-start gap-2 text-sm">
            <Checkbox checked={form.epk} onCheckedChange={(v) => set({ epk: Boolean(v) })} aria-label={t('retention.form.epk')} />
            <span>
              {t('retention.form.epk')}
              <span className="block text-xs text-muted-foreground">{t('retention.form.epkHint')}</span>
            </span>
          </label>
          <div className="space-y-1.5">
            <Label htmlFor="retention-note">{t('retention.form.note')}</Label>
            <Textarea id="retention-note" rows={2} maxLength={LIMITS.note} value={form.note}
              onChange={(e) => set({ note: e.target.value })} {...checks.describe('note', 'retention')} />
            <FieldError id="retention-note-error" message={errors.note} />
          </div>
          {initial?.cases > 0 && (
            <p className="rounded-md border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-2 text-xs text-amber-800 dark:text-amber-300">
              {t('retention.form.inUse', { count: initial.cases })}
            </p>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>{t('confirm.cancel')}</Button>
          <Button onClick={submit} disabled={busy || !valid} data-testid="retention-class-save">
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t('retention.form.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SectionsDialog({ open, onOpenChange, sections, onChanged }) {
  const { t, i18n } = useTranslation('console');
  const [editing, setEditing] = useState(null); // {uuid?, code, title, position}
  const [busy, setBusy] = useState(false);
  const checks = useFieldErrors(editing ? sectionProblems(editing) : {});
  const { reset } = checks;

  useEffect(() => {
    if (!open) setEditing(null);
  }, [open]);
  const edit = (next) => {
    setEditing(next);
    reset();
  };
  const change = (patch) => {
    setEditing((prev) => ({ ...prev, ...patch }));
    Object.keys(patch).forEach((key) => checks.touch(key));
  };

  const save = async () => {
    if (!checks.attempt()) return;
    setBusy(true);
    const body = { code: editing.code.trim(), title: editing.title, position: Number(editing.position) || 0 };
    const res = editing.uuid
      ? await api.patch(ep.retentionSection(editing.uuid), { data: body })
      : await api.post(ep.retentionSections(), { data: body });
    setBusy(false);
    if (res.success) {
      toast.success(t('retention.sections.saved'));
      setEditing(null);
      onChanged();
    } else {
      checks.showServer(res, SECTION_FIELDS);
    }
  };
  const remove = async (section) => {
    const res = await api.del(ep.retentionSection(section.uuid));
    if (res.success) {
      toast.success(t('retention.sections.deleted'));
      onChanged();
    } else {
      toast.error(res.message);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !busy && onOpenChange(o)}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t('retention.sections.title')}</DialogTitle>
          <DialogDescription>{t('retention.sections.description')}</DialogDescription>
        </DialogHeader>
        {editing ? (
          <div className="space-y-3">
            <div className="grid gap-3 sm:grid-cols-[8rem_8rem]">
              <div className="space-y-1.5">
                <Label htmlFor="section-code" className={REQUIRED_LABEL}>{t('retention.sections.code')}</Label>
                <Input id="section-code" required maxLength={LIMITS.sectionCode} value={editing.code}
                  onChange={(e) => change({ code: e.target.value })} {...checks.describe('code', 'section')} />
                <FieldError id="section-code-error" message={checks.errors.code} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="section-position">{t('retention.sections.position')}</Label>
                <Input id="section-position" type="number" min={0} max={LIMITS.positionMax} step={1} value={editing.position}
                  onChange={(e) => change({ position: e.target.value })} {...checks.describe('position', 'section')} />
                <FieldError id="section-position-error" message={checks.errors.position} />
              </div>
            </div>
            <TitleFields value={editing.title} onChange={(title) => change({ title })} idPrefix="section-title" errors={checks.errors} />
            <DialogFooter>
              <Button variant="outline" onClick={() => edit(null)} disabled={busy}>{t('confirm.cancel')}</Button>
              <Button onClick={save} disabled={busy || !editing.code.trim() || !hasTitle(editing.title) || checks.blocked} data-testid="retention-section-save">
                {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {t('retention.form.save')}
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-3">
            {sections.length === 0 ? (
              <p className="py-4 text-center text-sm text-muted-foreground">{t('retention.sections.empty')}</p>
            ) : (
              <ul className="max-h-80 divide-y overflow-y-auto rounded-md border">
                {sections.map((s) => (
                  <li key={s.uuid} className="flex items-center gap-3 px-3 py-2 text-sm">
                    <span className="w-12 shrink-0 font-mono text-xs text-muted-foreground">{s.code}</span>
                    <span className="flex-1">{titleIn(s.title, i18n.language)}</span>
                    <span className="text-xs text-muted-foreground">{t('retention.sections.classes', { count: s.classes })}</span>
                    <Button variant="ghost" size="icon" aria-label={t('retention.edit')} onClick={() => edit({ ...s, title: { ...s.title } })}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" aria-label={t('retention.delete')} disabled={s.classes > 0} onClick={() => remove(s)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </li>
                ))}
              </ul>
            )}
            <DialogFooter>
              <Button variant="outline" className="gap-1.5" onClick={() => edit({ code: '', title: {}, position: (sections.length + 1) * 10 })}>
                <Plus className="h-4 w-4" /> {t('retention.sections.add')}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ImportSummary({ result }) {
  const { t } = useTranslation('console');
  if (!result) return null;
  return (
    <div className="space-y-2 rounded-md border bg-muted/50 p-3 text-sm" data-testid="retention-import-summary">
      <p>
        {t('retention.import.summary', {
          added: result.added_classes,
          updated: result.updated_classes,
          skipped: result.skipped,
          sections: result.added_sections,
        })}
      </p>
      {result.errors?.length > 0 && (
        <ul className="max-h-40 space-y-1 overflow-y-auto text-xs text-red-700 dark:text-red-300">
          {result.errors.map((e) => (
            <li key={`${e.line}-${e.error}`}>
              {t('retention.import.errorLine', { line: e.line, article: e.article || '—' })}{' '}
              {t(`retention.import.errors.${e.error}`, { defaultValue: e.error, value: e.value })}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function StandardImportDialog({ open, onOpenChange, onImported }) {
  const { t, i18n } = useTranslation('console');
  const [overview, setOverview] = useState(null);
  const [chosen, setChosen] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setOverview(null);
    api.get(ep.retentionStandard()).then((res) => {
      if (cancelled) return;
      if (res.success) {
        setOverview(res.response);
        setChosen((res.response.sections || []).filter((s) => s.present < s.classes).map((s) => s.code));
      } else {
        toast.error(res.message);
        onOpenChange(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [open, onOpenChange]);

  const toggle = (code) => setChosen((prev) => (prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]));
  const submit = async () => {
    setBusy(true);
    const res = await api.post(ep.retentionStandardImport(), { data: { sections: chosen } });
    setBusy(false);
    if (res.success) {
      toast.success(t('retention.import.done', { count: res.response.added_classes }));
      onImported();
      onOpenChange(false);
    } else {
      toast.error(res.message);
    }
  };
  const source = overview?.source || {};

  return (
    <Dialog open={open} onOpenChange={(o) => !busy && onOpenChange(o)}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t('retention.standard.title')}</DialogTitle>
          <DialogDescription>{t('retention.standard.description')}</DialogDescription>
        </DialogHeader>
        {!overview ? (
          <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
        ) : !overview.available ? (
          <p className="py-4 text-sm text-muted-foreground" data-testid="retention-standard-unavailable">{t('retention.standard.unavailable')}</p>
        ) : (
          <div className="space-y-3">
            <div className="rounded-md border bg-muted/50 p-3 text-xs text-muted-foreground">
              <p className="font-medium text-foreground">{source.title}</p>
              {[source.issuer, source.number, source.approved].filter(Boolean).join(' · ')}
              {source.status && source.status !== 'in force' && (
                <Badge variant="outline" className="ml-2 border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300">{source.status}</Badge>
              )}
            </div>
            <ul className="max-h-72 divide-y overflow-y-auto rounded-md border">
              {overview.sections.map((s) => (
                <li key={s.code} className="flex items-center gap-3 px-3 py-2 text-sm">
                  <Checkbox
                    checked={chosen.includes(s.code)}
                    onCheckedChange={() => toggle(s.code)}
                    disabled={s.present >= s.classes}
                    aria-label={titleIn(s.title, i18n.language)}
                  />
                  <span className="w-10 shrink-0 font-mono text-xs text-muted-foreground">{s.code}</span>
                  <span className="flex-1">{titleIn(s.title, i18n.language)}</span>
                  <span className="text-xs text-muted-foreground">{t('retention.standard.present', { present: s.present, total: s.classes })}</span>
                </li>
              ))}
            </ul>
            <p className="text-xs text-muted-foreground">{t('retention.standard.keeps')}</p>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>{t('confirm.cancel')}</Button>
          {overview?.available && (
            <Button onClick={submit} disabled={busy || chosen.length === 0} data-testid="retention-standard-import">
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t('retention.standard.import', { count: chosen.length })}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FileImportDialog({ open, onOpenChange, onImported }) {
  const { t } = useTranslation('console');
  const [file, setFile] = useState(null);
  const [check, setCheck] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setFile(null);
      setCheck(null);
    }
  }, [open]);

  const send = async (dryRun) => {
    const form = new FormData();
    form.append('file', file);
    setBusy(true);
    const res = await api.post(ep.retentionImport(), { data: form, params: { dry_run: dryRun } });
    setBusy(false);
    if (!res.success) {
      toast.error(res.message);
      return;
    }
    if (dryRun) {
      setCheck(res.response);
    } else {
      toast.success(t('retention.import.done', { count: res.response.added_classes + res.response.updated_classes }));
      onImported();
      onOpenChange(false);
    }
  };
  const ready = check && !check.errors?.length;

  return (
    <Dialog open={open} onOpenChange={(o) => !busy && onOpenChange(o)}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('retention.import.title')}</DialogTitle>
          <DialogDescription>{t('retention.import.description')}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Input
            type="file"
            accept=".xlsx,.csv"
            aria-label={t('retention.import.file')}
            onChange={(e) => {
              setFile(e.target.files?.[0] || null);
              setCheck(null);
            }}
          />
          <p className="text-xs text-muted-foreground">{t('retention.import.columns')}</p>
          <ImportSummary result={check} />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>{t('confirm.cancel')}</Button>
          {ready ? (
            <Button onClick={() => send(false)} disabled={busy} data-testid="retention-import-apply">
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t('retention.import.apply')}
            </Button>
          ) : (
            <Button onClick={() => send(true)} disabled={busy || !file} data-testid="retention-import-check">
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t('retention.import.check')}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function RetentionTab() {
  const { t, i18n } = useTranslation(['console', 'records']);
  const [data, setData] = useState(null);
  const [q, setQ] = useState('');
  const [section, setSection] = useState(ALL);
  const [status, setStatus] = useState(ALL);
  const [source, setSource] = useState(ALL);
  const [form, setForm] = useState({ open: false, initial: null });
  const [dialog, setDialog] = useState(null); // 'sections' | 'standard' | 'file'
  const [doomed, setDoomed] = useState(null);
  const [reapply, setReapply] = useState(null); // { uuid, article, count }

  const load = useCallback(async () => {
    const res = await api.get(ep.retention());
    if (res.success) setData(res.response);
    else toast.error(res.message);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const sections = useMemo(() => data?.sections || [], [data]);
  const sectionOf = useMemo(() => Object.fromEntries(sections.map((s) => [s.uuid, s])), [sections]);
  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (data?.classes || []).filter((c) => {
      if (section !== ALL && String(c.section_uuid ?? NO_SECTION) !== section) return false;
      if (status !== ALL && c.active !== (status === 'active')) return false;
      if (source !== ALL && c.source !== source) return false;
      if (!needle) return true;
      return [c.article, c.note, ...Object.values(c.title || {})].some((v) => (v || '').toLowerCase().includes(needle));
    });
  }, [data, q, section, status, source]);

  const closeDialog = useCallback(() => setDialog(null), []);

  const save = async (body) => {
    const editing = form.initial;
    const res = editing
      ? await api.patch(ep.retentionClass(editing.uuid), { data: body })
      : await api.post(ep.retentionClasses(), { data: body });
    if (res.success) {
      toast.success(t('retention.saved'));
      setForm({ open: false, initial: null });
      load();
      // Closed cases keep the period they closed with; offer to re-fix them.
      const periodChanged = editing && (editing.period_kind !== body.period_kind || editing.period_years !== body.period_years);
      if (periodChanged && editing.closed_cases > 0) {
        setReapply({ uuid: editing.uuid, article: editing.article, count: editing.closed_cases });
      }
    }
    return res; // the form shows a refusal beside its field
  };
  const setActive = async (row, active) => {
    const res = await api.patch(ep.retentionClass(row.uuid), { data: { active } });
    if (res.success) {
      toast.success(t(active ? 'retention.activated' : 'retention.deactivated'));
      load();
    } else {
      toast.error(res.message);
    }
  };
  const remove = async () => {
    const res = await api.del(ep.retentionClass(doomed.uuid));
    if (res.success) {
      toast.success(t('retention.deleted'));
      load();
    } else {
      toast.error(res.message);
    }
  };
  const exportAs = async (format) => {
    const res = await downloadFile(ep.retentionExport(), { params: { format }, filename: `retention.${format}` });
    if (!res.success) toast.error(res.message);
  };

  if (!data) {
    return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  }

  return (
    <div className="space-y-4" data-testid="retention-tab">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="max-w-2xl">
          <h2 className="text-lg font-semibold text-foreground">{t('retention.title')}</h2>
          <p className="text-sm text-muted-foreground">{t('retention.description')}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="gap-1.5"><FileUp className="h-4 w-4" /> {t('retention.import.menu')}</Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onSelect={() => setDialog('standard')}><Library className="mr-2 h-4 w-4" /> {t('retention.standard.menu')}</DropdownMenuItem>
              <DropdownMenuItem onSelect={() => setDialog('file')}><FileUp className="mr-2 h-4 w-4" /> {t('retention.import.fromFile')}</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="gap-1.5" disabled={!data.classes.length}><Download className="h-4 w-4" /> {t('retention.export')}</Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onSelect={() => exportAs('xlsx')}>Excel (.xlsx)</DropdownMenuItem>
              <DropdownMenuItem onSelect={() => exportAs('csv')}>CSV</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button variant="outline" className="gap-1.5" onClick={() => setDialog('sections')}>
            <Rows3 className="h-4 w-4" /> {t('retention.sections.menu')}
          </Button>
          <Button className="gap-1.5" onClick={() => setForm({ open: true, initial: null })} data-testid="retention-add">
            <Plus className="h-4 w-4" /> {t('retention.add')}
          </Button>
        </div>
      </div>

      {data.classes.length === 0 ? (
        <div className="rounded-lg border border-dashed p-10 text-center" data-testid="retention-empty">
          <Archive className="mx-auto mb-3 h-8 w-8 text-muted-foreground" />
          <p className="font-medium text-foreground">{t('retention.empty.title')}</p>
          <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">{t('retention.empty.description')}</p>
          <div className="mt-4 flex justify-center gap-2">
            <Button variant="outline" onClick={() => setDialog('standard')}>{t('retention.standard.menu')}</Button>
            <Button onClick={() => setForm({ open: true, initial: null })}>{t('retention.add')}</Button>
          </div>
        </div>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <Input className="h-9 w-64" placeholder={t('retention.search')} value={q} onChange={(e) => setQ(e.target.value)} aria-label={t('retention.search')} />
            <Select value={section} onValueChange={setSection}>
              <SelectTrigger className="h-9 w-56" aria-label={t('retention.form.section')}><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>{t('retention.filters.allSections')}</SelectItem>
                {sections.map((s) => (
                  <SelectItem key={s.uuid} value={String(s.uuid)}>{s.code} · {titleIn(s.title, i18n.language)}</SelectItem>
                ))}
                <SelectItem value={NO_SECTION}>{t('retention.noSection')}</SelectItem>
              </SelectContent>
            </Select>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="h-9 w-40" aria-label={t('retention.filters.status')}><SelectValue /></SelectTrigger>
              <SelectContent>
                {[ALL, 'active', 'inactive'].map((s) => <SelectItem key={s} value={s}>{t(`retention.filters.statuses.${s}`)}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={source} onValueChange={setSource}>
              <SelectTrigger className="h-9 w-44" aria-label={t('retention.filters.source')}><SelectValue /></SelectTrigger>
              <SelectContent>
                {[ALL, 'standard', 'custom'].map((s) => <SelectItem key={s} value={s}>{t(`retention.filters.sources.${s}`)}</SelectItem>)}
              </SelectContent>
            </Select>
            <span className="ml-auto text-xs text-muted-foreground">{t('retention.count', { count: rows.length })}</span>
          </div>

          <div className="overflow-x-auto rounded-md border">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
                <tr>
                  <th className="px-3 py-2">{t('retention.columns.article')}</th>
                  <th className="px-3 py-2">{t('retention.columns.title')}</th>
                  <th className="px-3 py-2">{t('retention.columns.period')}</th>
                  <th className="px-3 py-2">{t('retention.columns.section')}</th>
                  <th className="px-3 py-2">{t('retention.columns.cases')}</th>
                  <th className="px-3 py-2" />
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.uuid} className={`border-t ${row.active ? '' : 'text-muted-foreground'}`} data-testid={`retention-row-${row.article}`}>
                    <td className="px-3 py-2 font-mono text-xs">{row.article}</td>
                    <td className="px-3 py-2">
                      {titleIn(row.title, i18n.language)}
                      {row.note && <span className="block text-xs text-muted-foreground">{row.note}</span>}
                      <span className="mt-1 flex gap-1">
                        {row.source === 'standard' && <Badge variant="outline">{t('retention.filters.sources.standard')}</Badge>}
                        {!row.active && <Badge variant="outline">{t('retention.filters.statuses.inactive')}</Badge>}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-3 py-2">{periodLabel(row, t)}</td>
                    <td className="px-3 py-2 text-xs text-muted-foreground">{sectionOf[row.section_uuid]?.code || '—'}</td>
                    <td className="px-3 py-2 tabular-nums">{row.cases}</td>
                    <td className="whitespace-nowrap px-3 py-2 text-right">
                      <Button variant="ghost" size="icon" aria-label={t('retention.edit')} onClick={() => setForm({ open: true, initial: row })}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label={t(row.active ? 'retention.deactivate' : 'retention.activate')}
                        title={t(row.active ? 'retention.deactivate' : 'retention.activate')}
                        onClick={() => setActive(row, !row.active)}
                      >
                        <Power className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label={t('retention.delete')}
                        title={row.cases > 0 ? t('retention.inUseNoDelete') : t('retention.delete')}
                        disabled={row.cases > 0}
                        onClick={() => setDoomed(row)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      <ClassForm
        open={form.open}
        onOpenChange={(open) => setForm((prev) => ({ ...prev, open }))}
        initial={form.initial}
        sections={sections}
        onSave={save}
      />
      <SectionsDialog open={dialog === 'sections'} onOpenChange={closeDialog} sections={sections} onChanged={load} />
      <StandardImportDialog open={dialog === 'standard'} onOpenChange={closeDialog} onImported={load} />
      <FileImportDialog open={dialog === 'file'} onOpenChange={closeDialog} onImported={load} />
      <ConfirmDialog
        open={Boolean(reapply)}
        onOpenChange={(open) => !open && setReapply(null)}
        title={t('retention.reapply.title', { article: reapply?.article })}
        description={t('retention.reapply.description', { count: reapply?.count || 0 })}
        confirmLabel={t('retention.reapply.confirm', { count: reapply?.count || 0 })}
        onConfirm={async () => {
          const res = await api.post(ep.retentionReapply(reapply.uuid));
          if (res.success) toast.success(t('retention.reapply.done', { count: res.response.updated }));
          else toast.error(res.message);
        }}
      />
      <ConfirmDialog
        open={Boolean(doomed)}
        onOpenChange={(open) => !open && setDoomed(null)}
        title={t('retention.deleteTitle', { article: doomed?.article })}
        description={t('retention.deleteDescription')}
        confirmLabel={t('retention.delete')}
        destructive
        onConfirm={remove}
      />
    </div>
  );
}
